import { useEffect } from "react";
import {
	CatalogPartFormContent,
	CatalogPartFormValues,
} from "src/components/views/erp/parts/catalogPart/CatalogPartFormContent.tsx";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import { AavoFormLayout } from "src/components/common/forms/AavoFormLayout.tsx";
import i18n from "i18next";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";
import { CatalogPartApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartApi.ts";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { concatWithPipe } from "src/utils/strings.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { PartCreationWizardApi_FormInitData } from "src/api/generated/erp/parts/catalogPart/api/partCreationWizardApi.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";

interface CatalogPartFormStepProps {
	catalogPart: Partial<CatalogPartFormValues>;
	initData: PartCreationWizardApi_FormInitData;
	updateCatalogPart: (updates: Partial<CatalogPartFormValues>) => void;
	onSelectTemplate: (templateCatalogPartId: number) => void;
	onValidationChange?: (isValid: boolean) => void;
	publishCatalogPartRevision: boolean;
	onUpdatePublishCatalogPartRevision: (release: boolean) => void;
}

export const CatalogPartFormStep = ({
	catalogPart: initialCatalogPart,
	initData,
	updateCatalogPart,
	onSelectTemplate,
	onValidationChange,
	publishCatalogPartRevision,
	onUpdatePublishCatalogPartRevision,
}: CatalogPartFormStepProps) => {
	const showFormDialog = useFormInput();

	const useAavoFormReturn = useAavoForm<CatalogPartFormValues, void>({
		defaultValues: initialCatalogPart,
		onSubmit: () => {},
	});

	const { watch, reset, getValues, formState, trigger } = useAavoFormReturn;

	useEffect(() => {
		const subscription = watch((newValues) => {
			updateCatalogPart(newValues);
		});
		return () => subscription.unsubscribe();
	}, [watch, updateCatalogPart]);

	useEffect(() => {
		onValidationChange?.(formState.isValid);
	}, [formState.isValid, formState.errors, onValidationChange, getValues]);

	useEffect(() => {
		trigger().catch(logError);
	}, [trigger]);

	return (
		<AavoFormLayout columns={2}>
			<AsyncButton
				label={i18n.t("import_from_template")}
				icon={faArrowDownToLine}
				sx={{ marginBottom: 1 }}
				onClick={handleSelectTemplate}
			/>
			<CatalogPartFormContent initData={initData} isNewRecord {...useAavoFormReturn} />
			<AavoCheckbox
				label={i18n.t("publish_catalog_part_revision")}
				checked={publishCatalogPartRevision}
				onChange={(checked) => onUpdatePublishCatalogPartRevision(checked)}
				sx={{ gridColumn: "1/-1" }}
			/>
		</AavoFormLayout>
	);

	async function handleSelectTemplate() {
		const formResult = await showFormDialog<{ catalogPart: CatalogPart }>({
			title: i18n.t("select_template_part"),
			size: "sm",
			defaultValues: {
				catalogPart: undefined,
			},
			submitOnEnter: true,
			content: ({ control }) => (
				<FormAsyncSelectField
					control={control}
					name={"catalogPart"}
					formValueType={"option"}
					label={i18n.t("template")}
					fetchOptions={async ({ searchQuery, currentSelection }) =>
						CatalogPartApi.getCatalogPartOptions({
							searchQuery,
							currentSelection,
						})
					}
					getOptionKey={(option: CatalogPart) => option.catalogPartId}
					getOptionLabel={(option) =>
						concatWithPipe(option.partNo, option.description_1, option.description_2)
					}
					rules={requireRule()}
					autoFocus
				/>
			),
		});
		if (formResult === undefined) return;

		const formValues = getValues();
		// Keep existing category if defined
		const newCategoryId = formValues.categoryId ?? formResult.catalogPart.categoryId;
		reset({
			numberSequenceId: formValues.numberSequenceId,
			partNumberInsertType: formValues.partNumberInsertType,
			manuallyInsertedPartNumber: formValues.manuallyInsertedPartNumber,
			...formResult.catalogPart,
			categoryId: newCategoryId,
		});

		onSelectTemplate(formResult.catalogPart.catalogPartId);
		await trigger();
	}
};
